import { Component } from 'react';
import { Button } from './ui/button';
import { AlertTriangle, RefreshCw, LifeBuoy } from 'lucide-react';
import ReportIssueModal from './support/ReportIssueModal';

/**
 * App-wide error boundary — catches runtime errors in any descendant tree
 * (rendering, lifecycle, hooks) and shows a themed fallback instead of a
 * blank white screen.
 *
 * Mounted at the top of `App.js` outside the routers so the fallback
 * persists even if Router itself errors. Theme classes (`bg-theme`,
 * `text-theme-text`, `bg-theme-secondary`) make the fallback respect
 * light/dark mode automatically — no extra wiring.
 *
 * Strategy:
 *   • `getDerivedStateFromError` flips us into the fallback view immediately.
 *   • `componentDidCatch` logs to the browser console (and would forward
 *      to Sentry if/when it's wired).
 *   • **Chunk-load recovery** (added 2026-05-13): when the user's browser
 *      tab has a stale `main.js` referencing now-deleted chunk filenames
 *      (typical after a redeploy / hot-reload during dev), `React.lazy()`
 *      rejects with `ChunkLoadError` and the dev server returns
 *      `index.html` for the missing `/static/js/...` URL, producing
 *      "Unexpected token '<'" syntax errors. We detect that shape and
 *      do a one-time hard reload so the browser fetches the new
 *      `main.js` with current chunk hashes. The one-time guard via
 *      sessionStorage prevents an infinite reload loop if reloading
 *      doesn't actually fix it (e.g. the chunk really is permanently
 *      missing — in that case we fall through to the normal fallback).
 *   • Reload triggers a full `window.location.reload()` — clears React
 *     state and SW state without needing route knowledge.
 *   • Report issue reuses the existing `ReportIssueModal` so support
 *     tickets carry the same UX as in-app reports.
 */

const CHUNK_RELOAD_SESSION_KEY = 'ccuk-chunk-reload-attempted';

/** Returns true if the given error looks like a stale lazy-chunk failure. */
function isChunkLoadError(error) {
  if (!error) return false;
  const name = String(error.name || '');
  const message = String(error.message || '');
  if (/ChunkLoadError/i.test(name)) return true;
  if (/Loading chunk [\w-]+ failed/i.test(message)) return true;
  if (/Loading CSS chunk [\w-]+ failed/i.test(message)) return true;
  // Dynamic import served HTML fallback (SPA catch-all returning
  // index.html for a missing /static/js/*.js URL) → SyntaxError.
  if (/Unexpected token '<'/i.test(message)) return true;
  if (/Failed to fetch dynamically imported module/i.test(message)) return true;
  return false;
}

export default class AppErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, showReport: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error, showReport: false };
  }

  componentDidCatch(error, info) {
    if (isChunkLoadError(error)) {
      try {
        const alreadyTried = window.sessionStorage.getItem(CHUNK_RELOAD_SESSION_KEY);
        if (!alreadyTried) {
          window.sessionStorage.setItem(CHUNK_RELOAD_SESSION_KEY, String(Date.now()));
          // eslint-disable-next-line no-console
          console.warn('[AppErrorBoundary] Detected stale chunk — reloading once.');
          window.location.reload();
          return;
        }
      } catch { /* sessionStorage unavailable — fall through */ }
    }
    // eslint-disable-next-line no-console
    console.error('[AppErrorBoundary] caught error:', error, info?.componentStack);
  }

  componentDidMount() {
    // Catch lazy-import promise rejections that aren't inside a React
    // commit phase (so getDerivedStateFromError won't fire). Same
    // recovery as above — one hard reload, guarded by sessionStorage.
    this._onUnhandledRejection = (event) => {
      if (isChunkLoadError(event.reason)) {
        try {
          const alreadyTried = window.sessionStorage.getItem(CHUNK_RELOAD_SESSION_KEY);
          if (!alreadyTried) {
            window.sessionStorage.setItem(CHUNK_RELOAD_SESSION_KEY, String(Date.now()));
            // eslint-disable-next-line no-console
            console.warn('[AppErrorBoundary] Detected stale chunk in unhandled rejection — reloading.');
            event.preventDefault?.();
            window.location.reload();
          }
        } catch { /* sessionStorage unavailable */ }
      }
    };
    window.addEventListener('unhandledrejection', this._onUnhandledRejection);

    // On successful render (no error), clear the recovery flag so future
    // legitimate errors aren't suppressed by a stale sessionStorage entry.
    if (!this.state.hasError) {
      try { window.sessionStorage.removeItem(CHUNK_RELOAD_SESSION_KEY); } catch { /* noop */ }
    }
  }

  componentWillUnmount() {
    if (this._onUnhandledRejection) {
      window.removeEventListener('unhandledrejection', this._onUnhandledRejection);
    }
  }

  handleReload = () => {
    try { window.sessionStorage.removeItem(CHUNK_RELOAD_SESSION_KEY); } catch { /* noop */ }
    try { window.location.reload(); } catch { /* ignore */ }
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        className="min-h-screen w-full bg-theme text-theme-text flex items-center justify-center px-6 py-10"
        data-testid="app-error-boundary-fallback"
      >
        <div className="max-w-md w-full bg-theme-secondary border border-theme rounded-2xl shadow-xl p-8 text-center">
          <div className="mx-auto mb-5 h-14 w-14 rounded-2xl bg-red-500/10 flex items-center justify-center">
            <AlertTriangle className="h-7 w-7 text-red-500" aria-hidden="true" />
          </div>
          <h1 className="text-2xl font-bold tracking-tight mb-2">Something went wrong</h1>
          <p className="text-sm text-theme-muted mb-6 leading-relaxed">
            We hit an unexpected error. Reloading the page usually fixes it. If the problem keeps happening, please let us know.
          </p>
          <div className="flex flex-col sm:flex-row gap-2 justify-center">
            <Button
              onClick={this.handleReload}
              className="bg-theme-primary hover:bg-theme-primary-hover text-white gap-2"
              data-testid="error-boundary-reload"
            >
              <RefreshCw className="h-4 w-4" />
              Reload page
            </Button>
            <Button
              variant="outline"
              onClick={() => this.setState({ showReport: true })}
              className="border-theme text-theme-text gap-2"
              data-testid="error-boundary-report"
            >
              <LifeBuoy className="h-4 w-4" />
              Report an issue
            </Button>
          </div>
          {this.state.error?.message && (
            <p className="mt-6 text-[11px] text-theme-muted/70 font-mono break-words">
              {String(this.state.error.message).slice(0, 200)}
            </p>
          )}
        </div>
        {this.state.showReport && (
          <ReportIssueModal onClose={() => this.setState({ showReport: false })} />
        )}
      </div>
    );
  }
}
